import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useEffect, useState } from 'react'
import ECharts from '~/components/echarts'
import {
  awsSummarySavingsOrganizationOptimizerAWSMasterOptimizationsPath,
  opensearchCostAnalysisChartOrganizationOptimizerAWSMasterOptimizationsPath,
  opensearchDataNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  opensearchMasterNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  opensearchOptimizedDataNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  opensearchOptimizedMasterNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath,
  opensearchRiChartOrganizationOptimizerAWSMasterOptimizationsPath,
} from '~/routes'
import { csrfHeader } from '~/utils/csrf'

export default function OpensearchOptimization({ organizationId, masterOptimizationId, masterOptimization }) {
  const [activeTab, setActiveTab] = useState('daily-cost')
  const [savingsData, setSavingsData] = useState(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  useEffect(() => {
    if (masterOptimization) {
      fetchSavingsData()
    }
  }, [masterOptimization])

  const fetchSavingsData = async () => {
    try {
      const savingsUrl = awsSummarySavingsOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)
      const response = await fetch(savingsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeader(),
        },
        body: JSON.stringify({ master_optimization_id: masterOptimizationId }),
      })

      if (response.ok) {
        const data = await response.json()
        setSavingsData(data)
      } else {
        console.error('Failed to fetch OpenSearch savings data:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('Error fetching OpenSearch savings data:', error)
    }
  }

  if (!masterOptimization) {
    return null
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
  }

  const dailySavings = savingsData?.opensearch_savings?.daily_savings || 0
  const savingsPercentage = savingsData?.opensearch_savings?.savings_percentage || 0

  return (
    <div className='row mb-4'>
      <div className='col-12'>
        <div className='card'>
          <div className='card-header'>
            <h5 className='card-title mb-0'>
              <Trans>
                OpenSearch Optimization - Daily Savings ${dailySavings.toFixed(2)} ({savingsPercentage.toFixed(1)}%)
              </Trans>
            </h5>
          </div>
          <div className='card-body'>
            <ul className='nav nav-tabs mb-3'>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'daily-cost' ? 'active' : ''}`}
                  onClick={() => handleTabChange('daily-cost')}
                  type='button'
                >
                  <Trans>Daily Cost</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'data-nodes' ? 'active' : ''}`}
                  onClick={() => handleTabChange('data-nodes')}
                  type='button'
                >
                  <Trans>Data Nodes</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'master-nodes' ? 'active' : ''}`}
                  onClick={() => handleTabChange('master-nodes')}
                  type='button'
                >
                  <Trans>Master Nodes</Trans>
                </button>
              </li>
              <li className='nav-item'>
                <button
                  className={`nav-link ${activeTab === 'opensearch-ris' ? 'active' : ''}`}
                  onClick={() => handleTabChange('opensearch-ris')}
                  type='button'
                >
                  <Trans>OpenSearch RIs</Trans>
                </button>
              </li>
            </ul>

            <div className='mt-3'>
              {activeTab === 'daily-cost' && (
                <div className='wider-chart-container'>
                  <ECharts
                    title={t`Daily Cost Comparison`}
                    chartInfoTitle='aws_opensearch_optimization_daily_cost_comparison'
                    url={opensearchCostAnalysisChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                    params={{ master_optimization_id: masterOptimizationId }}
                  />
                </div>
              )}

              {activeTab === 'data-nodes' && (
                <div className='row'>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Current Data Node Types`}
                      chartInfoTitle='aws_opensearch_current_data_node_types'
                      url={opensearchDataNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Optimized Data Node Types`}
                      chartInfoTitle='aws_opensearch_optimized_data_node_types'
                      url={opensearchOptimizedDataNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath(
                        organizationId,
                      )}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'master-nodes' && (
                <div className='row'>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Current Master Node Types`}
                      chartInfoTitle='aws_opensearch_current_master_node_types'
                      url={opensearchMasterNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath(
                        organizationId,
                      )}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                  <div className='col-md-6'>
                    <ECharts
                      title={t`Optimized Master Node Types`}
                      chartInfoTitle='aws_opensearch_optimized_master_node_types'
                      url={opensearchOptimizedMasterNodeTypeChartsOrganizationOptimizerAWSMasterOptimizationsPath(
                        organizationId,
                      )}
                      params={{ master_optimization_id: masterOptimizationId }}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'opensearch-ris' && (
                <ECharts
                  title={t`OpenSearch Reserved Instances`}
                  chartInfoTitle='aws_opensearch_reserved_instances'
                  url={opensearchRiChartOrganizationOptimizerAWSMasterOptimizationsPath(organizationId)}
                  params={{ master_optimization_id: masterOptimizationId }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
